import { useState } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileText, Plus, Search, Edit, Eye, Calendar, User, DollarSign, X, Trash2, Printer, SlidersHorizontal } from "lucide-react";
import { MobileTable } from "@/components/ui/mobile-table";
import { useOrdemServico, Procedimento } from "@/hooks/useOrdemServico";
import { usePacientes } from "@/hooks/usePacientes";
import { useDentistas } from "@/hooks/useDentistas";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { formatLocalDateBR, parseLocalDate, todayLocalISO } from "@/lib/date-utils";
import { useActiveClinicaId } from "@/hooks/useActiveClinicaId";
import { EnviarDocumentoPaciente } from "@/components/documentos/EnviarDocumentoPaciente";
import { useInformacoesClinica } from "@/hooks/useInformacoesClinica";
import { useClinicaDocumento } from "@/hooks/useClinicaDocumento";
import { clinicBrandHeaderHtml, cloudOdontoSignatureHtml } from "@/lib/documentPrint";

export function OrdemServico() {
  const { informacoes } = useInformacoesClinica();
  const { clinicaAtual } = useClinicaDocumento(informacoes);
  const activeClinicaId = useActiveClinicaId();
  const { ordensServico, loading, createOrdemServico, updateOrdemServico, deleteOrdemServico } = useOrdemServico();
  const { pacientes } = usePacientes();
  const { dentistas } = useDentistas();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOS, setEditingOS] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingOS, setViewingOS] = useState<any>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [dentistaFilter, setDentistaFilter] = useState("Todos");
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [desconto, setDesconto] = useState(0);

  const [newOS, setNewOS] = useState({
    paciente_id: "",
    dentista_id: "",
    data_abertura: todayLocalISO(),
    data_prevista: "",
    status: "Aberta",
    prioridade: "Normal",
    observacoes: "",
  });

  const [procedimentos, setProcedimentos] = useState<Procedimento[]>([]);
  const [currentProcedimento, setCurrentProcedimento] = useState<Procedimento>({
    descricao: "",
    valor: 0,
    dente: "",
  });

  const filteredOS = ordensServico.filter(os => {
    const paciente = pacientes.find(p => p.id === os.paciente_id);
    const dentista = dentistas.find(d => d.id === os.dentista_id);
    const searchLower = searchTerm.toLowerCase();
    
    const matchText = (
      os.numero_os.toLowerCase().includes(searchLower) ||
      paciente?.nome.toLowerCase().includes(searchLower) ||
      dentista?.nome.toLowerCase().includes(searchLower)
    );
    const matchStatus = statusFilter === "Todos" || os.status === statusFilter;
    const matchDentista = dentistaFilter === "Todos" || os.dentista_id === dentistaFilter;
    const matchDataInicial = !dataInicial || os.data_abertura >= dataInicial;
    const matchDataFinal = !dataFinal || os.data_abertura <= dataFinal;
    return matchText && matchStatus && matchDentista && matchDataInicial && matchDataFinal;
  });

  const resumoOS = filteredOS.reduce(
    (acc, os) => ({
      quantidade: acc.quantidade + 1,
      valor: acc.valor + Number(os.valor_total || 0),
      abertas: acc.abertas + (os.status === "Aberta" ? 1 : 0),
      concluidas: acc.concluidas + (os.status === "Concluída" ? 1 : 0),
    }),
    { quantidade: 0, valor: 0, abertas: 0, concluidas: 0 }
  );

  const addProcedimento = () => {
    if (!currentProcedimento.descricao || currentProcedimento.valor <= 0) return;
    
    setProcedimentos([...procedimentos, currentProcedimento]);
    setCurrentProcedimento({
      descricao: "",
      valor: 0,
      dente: "",
    });
  };

  const removeProcedimento = (index: number) => {
    setProcedimentos(procedimentos.filter((_, i) => i !== index));
  };

  const calcularValorBruto = () => {
    return procedimentos.reduce((total, proc) => total + proc.valor, 0);
  };

  const calcularValorTotal = () => Math.max(calcularValorBruto() - Number(desconto || 0), 0);

  const handleSave = async () => {
    if (!newOS.paciente_id || !newOS.dentista_id || procedimentos.length === 0) {
      return;
    }

    try {
      const valorTotal = calcularValorTotal();
      
      if (editingOS) {
        await updateOrdemServico(editingOS.id, {
          ...newOS,
          procedimentos,
          valor_bruto: calcularValorBruto(),
          valor_desconto: Number(desconto || 0),
          valor_total: valorTotal,
        });
      } else {
        await createOrdemServico({
          ...newOS,
          procedimentos,
          valor_bruto: calcularValorBruto(),
          valor_desconto: Number(desconto || 0),
          valor_total: valorTotal,
        });
      }

      resetForm();
      setDialogOpen(false);
    } catch (error) {
      console.error("Erro ao salvar OS:", error);
    }
  };

  const resetForm = () => {
    setNewOS({
      paciente_id: "",
      dentista_id: "",
      data_abertura: todayLocalISO(),
      data_prevista: "",
      status: "Aberta",
      prioridade: "Normal",
      observacoes: "",
    });
    setProcedimentos([]);
    setDesconto(0);
    setEditingOS(null);
  };

  const handleEdit = (os: any) => {
    setEditingOS(os);
    setNewOS({
      paciente_id: os.paciente_id,
      dentista_id: os.dentista_id,
      data_abertura: os.data_abertura,
      data_prevista: os.data_prevista || "",
      status: os.status,
      prioridade: os.prioridade,
      observacoes: os.observacoes || "",
    });
    setProcedimentos(os.procedimentos);
    setDesconto(Number(os.valor_desconto || 0));
    setDialogOpen(true);
  };

  const handleView = (os: any) => {
    setViewingOS(os);
    setViewDialogOpen(true);
  };

  const handleDelete = async () => {
    if (deleteId) {
      await deleteOrdemServico(deleteId);
      setDeleteId(null);
    }
  };

  const imprimirOS = (os: any) => {
    const paciente = pacientes.find(p => p.id === os.paciente_id);
    const dentista = dentistas.find(d => d.id === os.dentista_id);
    const procedimentosHtml = (os.procedimentos || []).map((proc: Procedimento) => `
      <tr>
        <td>${proc.descricao}</td>
        <td>${proc.dente || "-"}</td>
        <td style="text-align:right">R$ ${Number(proc.valor || 0).toFixed(2)}</td>
      </tr>
    `).join("");
    const html = `
      <html>
        <head>
          <title>${os.numero_os}</title>
          <style>
            body{font-family:Arial,sans-serif;color:#111;padding:24px}
            h1{font-size:22px;margin:0 0 8px}
            .muted{color:#555;font-size:12px}
            .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin:18px 0}
            table{width:100%;border-collapse:collapse;margin-top:16px}
            th,td{border:1px solid #ddd;padding:8px;font-size:13px}
            th{background:#f3f4f6;text-align:left}
            .totals{margin-top:16px;text-align:right;font-size:14px}
            .total{font-size:18px;font-weight:bold}
          </style>
        </head>
        <body>
          ${clinicBrandHeaderHtml(clinicaAtual)}
          <h1>Ordem de Serviço ${os.numero_os}</h1>
          <div class="muted">Emitida em ${formatLocalDateBR(todayLocalISO())}</div>
          <div class="grid">
            <div><strong>Paciente:</strong> ${paciente?.nome || "-"}</div>
            <div><strong>Dentista:</strong> ${dentista?.nome || "-"}</div>
            <div><strong>Abertura:</strong> ${parseLocalDate(os.data_abertura).toLocaleDateString("pt-BR")}</div>
            <div><strong>Status:</strong> ${os.status}</div>
          </div>
          <table>
            <thead><tr><th>Procedimento</th><th>Dente</th><th>Valor</th></tr></thead>
            <tbody>${procedimentosHtml}</tbody>
          </table>
          <div class="totals">
            <div>Subtotal: R$ ${Number(os.valor_bruto || os.valor_total || 0).toFixed(2)}</div>
            <div>Desconto: R$ ${Number(os.valor_desconto || 0).toFixed(2)}</div>
            <div class="total">Total: R$ ${Number(os.valor_total || 0).toFixed(2)}</div>
          </div>
          ${os.observacoes ? `<p><strong>Observações:</strong><br>${os.observacoes}</p>` : ""}
          ${cloudOdontoSignatureHtml()}
        </body>
      </html>
    `;
    const popup = window.open("", "_blank", "width=900,height=700");
    if (!popup) return;
    popup.document.write(html);
    popup.document.close();
    popup.focus();
    popup.print();
  };

  const limparFiltros = () => {
    setSearchTerm("");
    setStatusFilter("Todos");
    setDentistaFilter("Todos");
    setDataInicial("");
    setDataFinal("");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Concluída": return "default";
      case "Em Andamento": return "secondary";
      case "Aberta": return "outline";
      case "Cancelada": return "destructive";
      default: return "secondary";
    }
  };

  const columns = [
    { 
      key: 'numero_os' as const, 
      header: 'Número OS', 
      className: 'font-medium',
    },
    { 
      key: 'paciente_id' as const, 
      header: 'Paciente',
      render: (value: string) => pacientes.find(p => p.id === value)?.nome || 'N/A'
    },
    { 
      key: 'dentista_id' as const, 
      header: 'Dentista', 
      className: 'hidden md:table-cell',
      render: (value: string) => dentistas.find(d => d.id === value)?.nome || 'N/A'
    },
    { 
      key: 'status' as const, 
      header: 'Status',
      render: (value: string) => (
        <Badge variant={getStatusColor(value)}>
          {value}
        </Badge>
      )
    },
    { 
      key: 'valor_total' as const, 
      header: 'Valor', 
      className: 'hidden lg:table-cell font-medium',
      render: (value: number) => `R$ ${value.toFixed(2)}`
    },
    { 
      key: 'data_abertura' as const, 
      header: 'Data', 
      className: 'hidden lg:table-cell',
      render: (value: string) => parseLocalDate(value).toLocaleDateString('pt-BR')
    }
  ];

  const mobileCardRender = (os: any) => {
    const paciente = pacientes.find(p => p.id === os.paciente_id);
    const dentista = dentistas.find(d => d.id === os.dentista_id);
    
    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-medium text-base">{os.numero_os}</h3>
            <p className="text-sm text-muted-foreground">{paciente?.nome}</p>
          </div>
          <Badge variant={getStatusColor(os.status)}>
            {os.status}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <div className="flex items-center text-sm text-muted-foreground">
            <User className="w-4 h-4 mr-2 flex-shrink-0" />
            <span>{dentista?.nome}</span>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-muted-foreground">
              <DollarSign className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="font-medium">R$ {os.valor_total.toFixed(2)}</span>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>{parseLocalDate(os.data_abertura).toLocaleDateString('pt-BR')}</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderActions = (os: any) => (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleView(os)}>
        <Eye className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only sm:ml-1">Ver</span>
      </Button>
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => imprimirOS(os)}>
        <Printer className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only sm:ml-1">Imprimir</span>
      </Button>
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => handleEdit(os)}>
        <Edit className="w-4 h-4" />
        <span className="sr-only sm:not-sr-only sm:ml-1">Editar</span>
      </Button>
      <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setDeleteId(os.id)}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Ordem de Serviço</h1>
            <p className="text-muted-foreground">Gerencie as ordens de serviço da clínica</p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="gap-2 w-full sm:w-auto">
                <Plus className="w-4 h-4" />
                Nova OS
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingOS ? "Editar" : "Nova"} Ordem de Serviço</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {/* Paciente e Dentista */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="paciente">Paciente *</Label>
                    <SearchableSelect
                      value={newOS.paciente_id}
                      onValueChange={(value) => setNewOS({ ...newOS, paciente_id: value })}
                      placeholder="Busque pelo nome, CPF ou telefone"
                      searchPlaceholder="Buscar paciente..."
                      options={pacientes.map((paciente) => ({
                        value: paciente.id,
                        label: paciente.nome,
                        description: [paciente.cpf, paciente.telefone].filter(Boolean).join(" - "),
                      }))}
                    />
                  </div>

                  <div>
                    <Label htmlFor="dentista">Dentista *</Label>
                    <SearchableSelect
                      value={newOS.dentista_id}
                      onValueChange={(value) => setNewOS({ ...newOS, dentista_id: value })}
                      placeholder="Busque pelo nome, CRO ou especialidade"
                      searchPlaceholder="Buscar dentista..."
                      options={dentistas.map((dentista) => ({
                        value: dentista.id,
                        label: dentista.nome,
                        description: [dentista.cro, dentista.especialidade].filter(Boolean).join(" - "),
                      }))}
                    />
                  </div>
                </div>

                {/* Datas e Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="data_abertura">Data de Abertura *</Label>
                    <Input
                      id="data_abertura"
                      type="date"
                      value={newOS.data_abertura}
                      onChange={(e) => setNewOS({ ...newOS, data_abertura: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="data_prevista">Data Prevista</Label>
                    <Input
                      id="data_prevista"
                      type="date"
                      value={newOS.data_prevista}
                      onChange={(e) => setNewOS({ ...newOS, data_prevista: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select value={newOS.status} onValueChange={(value) => setNewOS({ ...newOS, status: value })}>
                      <SelectTrigger id="status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aberta">Aberta</SelectItem>
                        <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                        <SelectItem value="Concluída">Concluída</SelectItem>
                        <SelectItem value="Cancelada">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="prioridade">Prioridade</Label>
                    <Select value={newOS.prioridade} onValueChange={(value) => setNewOS({ ...newOS, prioridade: value })}>
                      <SelectTrigger id="prioridade">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Baixa">Baixa</SelectItem>
                        <SelectItem value="Normal">Normal</SelectItem>
                        <SelectItem value="Alta">Alta</SelectItem>
                        <SelectItem value="Urgente">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Adicionar Procedimentos */}
                <div className="space-y-4 border-t pt-4">
                  <h4 className="font-medium">Procedimentos *</h4>
                  
                  <div className="border rounded-md p-4 space-y-4 bg-muted/30">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="proc_descricao">Descrição *</Label>
                        <Input
                          id="proc_descricao"
                          value={currentProcedimento.descricao}
                          onChange={(e) => setCurrentProcedimento({ ...currentProcedimento, descricao: e.target.value })}
                          placeholder="Ex: Restauração em resina"
                        />
                      </div>
                      <div>
                        <Label htmlFor="proc_dente">Dente</Label>
                        <Input
                          id="proc_dente"
                          value={currentProcedimento.dente}
                          onChange={(e) => setCurrentProcedimento({ ...currentProcedimento, dente: e.target.value })}
                          placeholder="Ex: 11"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="proc_valor">Valor *</Label>
                        <MoneyInput
                          id="proc_valor"
                          value={currentProcedimento.valor || ""}
                          onValueChange={(valor) => setCurrentProcedimento({ ...currentProcedimento, valor })}
                          placeholder="Ex.: 1.000,00"
                        />
                      </div>
                      <div className="flex items-end">
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="w-full"
                          onClick={addProcedimento}
                          disabled={!currentProcedimento.descricao || currentProcedimento.valor <= 0}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Adicionar Procedimento
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Lista de Procedimentos */}
                  {procedimentos.length > 0 && (
                    <div className="space-y-2">
                      <h5 className="font-medium text-sm">Procedimentos Adicionados:</h5>
                      {procedimentos.map((proc, index) => (
                        <div key={index} className="flex items-start justify-between p-3 bg-muted rounded-md">
                          <div className="flex-1 text-sm">
                            <div className="font-medium">{proc.descricao}</div>
                            <div className="text-muted-foreground">
                              {proc.dente && `Dente ${proc.dente} • `}R$ {proc.valor.toFixed(2)}
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeProcedimento(index)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="grid gap-3 sm:grid-cols-[1fr_220px] sm:items-end pt-2">
                        <div>
                          <Label htmlFor="desconto_os">Desconto (R$)</Label>
                          <MoneyInput
                            id="desconto_os"
                            value={desconto || ""}
                            onValueChange={setDesconto}
                            placeholder="Ex.: 100,00"
                          />
                        </div>
                        <div className="rounded-md border bg-background p-3 text-right">
                          <div className="text-sm text-muted-foreground">Subtotal: R$ {calcularValorBruto().toFixed(2)}</div>
                          <div className="text-sm text-muted-foreground">Desconto: R$ {Number(desconto || 0).toFixed(2)}</div>
                          <div className="text-lg font-bold">Total: R$ {calcularValorTotal().toFixed(2)}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Observações */}
                <div>
                  <Label htmlFor="observacoes">Observações</Label>
                  <Textarea
                    id="observacoes"
                    value={newOS.observacoes}
                    onChange={(e) => setNewOS({ ...newOS, observacoes: e.target.value })}
                    placeholder="Observações adicionais..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setDialogOpen(false);
                    resetForm();
                  }}>
                    Cancelar
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={!newOS.paciente_id || !newOS.dentista_id || procedimentos.length === 0 || loading}
                  >
                    {editingOS ? "Atualizar" : "Criar"} OS
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <FileText className="w-5 h-5" />
              Lista de Ordens de Serviço
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-6 grid gap-3">
              <div className="grid gap-3 sm:grid-cols-4">
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Quantidade de OS</p>
                  <p className="text-xl font-semibold">{resumoOS.quantidade}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Valor total filtrado</p>
                  <p className="text-xl font-semibold">R$ {resumoOS.valor.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Abertas</p>
                  <p className="text-xl font-semibold">{resumoOS.abertas}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Concluídas</p>
                  <p className="text-xl font-semibold">{resumoOS.concluidas}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input 
                  placeholder="Buscar por número, paciente ou dentista..." 
                  className="pl-10" 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                </div>
                <Button variant="outline" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                  <SlidersHorizontal className="mr-2 h-4 w-4" />
                  Filtros
                </Button>
              </div>
              {showAdvancedFilters && (
                <div className="grid gap-3 rounded-md border bg-muted/20 p-3 md:grid-cols-5">
                  <div>
                    <Label>Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Todos">Todos</SelectItem>
                        <SelectItem value="Aberta">Aberta</SelectItem>
                        <SelectItem value="Em Andamento">Em Andamento</SelectItem>
                        <SelectItem value="Concluída">Concluída</SelectItem>
                        <SelectItem value="Cancelada">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Dentista</Label>
                    <Select value={dentistaFilter} onValueChange={setDentistaFilter}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Todos">Todos</SelectItem>
                        {dentistas.map((dentista) => <SelectItem key={dentista.id} value={dentista.id}>{dentista.nome}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Data inicial</Label>
                    <Input type="date" value={dataInicial} onChange={(e) => setDataInicial(e.target.value)} />
                  </div>
                  <div>
                    <Label>Data final</Label>
                    <Input type="date" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" className="w-full" onClick={limparFiltros}>Limpar</Button>
                  </div>
                </div>
              )}
            </div>

            <MobileTable
              data={filteredOS}
              columns={columns}
              mobileCardRender={mobileCardRender}
              actions={renderActions}
              loading={loading}
              emptyState={{
                icon: FileText,
                title: searchTerm ? "Nenhuma ordem de serviço encontrada" : "Nenhuma ordem de serviço cadastrada",
                description: searchTerm 
                  ? "Não encontramos ordens de serviço com os termos pesquisados. Tente ajustar os filtros."
                  : "Você ainda não possui ordens de serviço cadastradas. Crie sua primeira ordem de serviço para começar.",
                actionLabel: searchTerm ? undefined : "Nova Ordem de Serviço",
                onAction: searchTerm ? undefined : () => setDialogOpen(true)
              }}
            />
          </CardContent>
        </Card>
      </div>

      {/* Dialog de Visualização */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Detalhes da Ordem de Serviço</DialogTitle>
          </DialogHeader>
          {viewingOS && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <strong>Número OS:</strong> {viewingOS.numero_os}
                </div>
                <div>
                  <strong>Status:</strong> <Badge variant={getStatusColor(viewingOS.status)}>{viewingOS.status}</Badge>
                </div>
                <div>
                  <strong>Paciente:</strong> {pacientes.find(p => p.id === viewingOS.paciente_id)?.nome}
                </div>
                <div>
                  <strong>Dentista:</strong> {dentistas.find(d => d.id === viewingOS.dentista_id)?.nome}
                </div>
                <div>
                  <strong>Data Abertura:</strong> {parseLocalDate(viewingOS.data_abertura).toLocaleDateString('pt-BR')}
                </div>
                {viewingOS.data_prevista && (
                  <div>
                    <strong>Data Prevista:</strong> {parseLocalDate(viewingOS.data_prevista).toLocaleDateString('pt-BR')}
                  </div>
                )}
                <div>
                  <strong>Prioridade:</strong> {viewingOS.prioridade}
                </div>
              </div>

              <div>
                <strong className="block mb-2">Procedimentos:</strong>
                {viewingOS.procedimentos.map((proc: Procedimento, index: number) => (
                  <div key={index} className="p-3 bg-muted rounded-md mb-2">
                    <div className="font-medium">{proc.descricao}</div>
                    <div className="text-sm text-muted-foreground">
                      {proc.dente && `Dente ${proc.dente} • `}R$ {proc.valor.toFixed(2)}
                    </div>
                  </div>
                ))}
                <div className="text-right text-sm text-muted-foreground mt-2">
                  Subtotal: R$ {Number(viewingOS.valor_bruto || viewingOS.valor_total || 0).toFixed(2)}
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  Desconto: R$ {Number(viewingOS.valor_desconto || 0).toFixed(2)}
                </div>
                <div className="text-right font-bold text-lg">
                  Total: R$ {Number(viewingOS.valor_total || 0).toFixed(2)}
                </div>
              </div>

              {viewingOS.observacoes && (
                <div>
                  <strong className="block mb-2">Observações:</strong>
                  <p className="text-sm p-3 bg-muted rounded-md">{viewingOS.observacoes}</p>
                </div>
              )}
              <div className="flex flex-wrap justify-end gap-2">
                <EnviarDocumentoPaciente entidade="ordem_servico" documentoId={viewingOS.id} clinicaId={activeClinicaId} titulo={`Ordem de serviço ${viewingOS.numero_os || ""}`} paciente={pacientes.find((p) => p.id === viewingOS.paciente_id)} />
                <Button variant="outline" onClick={() => imprimirOS(viewingOS)}>
                  <Printer className="mr-2 h-4 w-4" />
                  Imprimir ordem
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta ordem de serviço? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
